import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { remark } from 'remark';
import strip from 'strip-markdown';
import { truncate } from 'lodash';
import AudienceFilters from '@common/components/audience-filters-preview';
import { useAppSelector } from '@common/hooks';
import { Trans, useTranslation } from 'react-i18next';
import ImageItemUser from '@common/components/image-item/user';
import LinkWrapper from '@common/components/linkWrapper';
import Profile from '@common/components/profile';
import Icon from '../../../../common/components/icon';
import Tooltip from '../../../../common/components/tooltip';
import * as usersSelector from '../../../core/selectors/users';
import * as messagesSelector from '../../selectors/messages';

const defaultUser = { full_name: '', profile_img: '' };

const PREVIEW_LENGTH = 60;

const MessageRow = ({ item, openMessage, audiencePreviewData }) => {
  const { t } = useTranslation();

  const formattedDate = item.publish_at
    ? moment(item.publish_at).format('D MMMM, HH:mm')
    : moment(item.created_at).format('D MMMM, HH:mm');
  const percentage = Math.min(Math.round((item.message.seen_count / item.reach_count) * 100), 100);

  const icons = [
    !item.processed && (
      <Tooltip key="processed" title={t('organisation:message_row_scheduled')}>
        <Icon type="access_time" />
      </Tooltip>
    ),
    item.message.attachments > 0 && <Icon key="attachments" type="document" />,
    item.message.poll && <Icon key="poll" type="poll" />,
  ].filter((i) => !!i);

  const [previewText, setPreviewText] = useState(null);

  const mentionedUsers = useAppSelector((state) => {
    return messagesSelector.getMetaUsers(state);
  });

  const { text } = item.message;
  useEffect(() => {
    remark().use(strip).process(text).then((file) => {
      setPreviewText(String(file));
    });
  }, [text, setPreviewText, mentionedUsers]);

  const messageText = useMemo(() => {
    if (!previewText) return '';

    const users = Object.values(mentionedUsers);
    if (!users.length) return truncate(previewText, { length: PREVIEW_LENGTH });

    const replacedText = users.reduce((acc, user) => {
      const mentionRegex = new RegExp(`@\\(${user.id}\\)`, 'g');
      return acc.replace(mentionRegex, user?.full_name || '');
    }, previewText);

    return truncate(replacedText, { length: PREVIEW_LENGTH });
  }, [previewText, mentionedUsers]);

  return [
    <>
      <div className="text">
        {icons}
        {
          text ?
            (typeof messageText === 'string' ? messageText : '') :
            t('organisation:message_row_no_text')
        }
      </div>
      <div>
        <small>
          <a role="button" onClick={() => openMessage(item.id)}>
            <Trans i18nKey="organisation:message_row_view_message" />
          </a>
        </small>
      </div>
      <AudienceFilters
        predicates={item?.audience?.predicates}
        {...audiencePreviewData}
      />
    </>,
    <div className="tw-flex tw-items-center tw-gap-4 tw-py-2">
      <div className="tw-flex tw-items-center tw-gap-1">
        <Tooltip title={t('organisation:communication_reach_description')}>
          <Icon type="person" className="tw-text-violet-500 tw-text-sm" />
        </Tooltip>
        <span className="tw-text-violet-500 tw-text-sm">{item.reach_count || 0}</span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-1">
        <Tooltip title={t('organisation:communication_seen_description')}>
          <Icon type="visibility_off" className="tw-text-green-500 tw-text-sm" />
        </Tooltip>
        <span className="tw-text-green-500 tw-text-sm">{percentage || 0}%</span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-1">
        <Icon type="highfive" className="tw-text-blue-500 tw-text-sm" />
        <span className="tw-text-blue-500 tw-text-sm">{item.message.likes_count || 0}</span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-1">
        <Icon type="chat" className="tw-text-orange-500 tw-text-sm" />
        <span className="tw-text-orange-500 tw-text-sm">{item.message.comments_count || 0}</span>
      </div>
    </div>,
    <LinkWrapper url={`/admin/users/${item.message?.user?.id}/about`} className="tw-flex tw-items-center tw-gap-2 hover:tw-underline hover:tw-text-inherit">
      <Profile size={30} enablePopover user={{ id: item?.message?.user?.id, full_name: item?.message?.user?.full_name, profile_img: item?.message?.user?.profile_img }} />
    </LinkWrapper>,
    <small>
      <Icon type="calendar_today" />
      {!item.processed && t('organisation:message_row_planned')}
      {' '}{formattedDate}
    </small>,
  ];
};

export default MessageRow;
